import { Button, Card, Col, Row, Space } from 'antd';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { observer } from 'mobx-react-lite';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import React, { useState, useEffect, useRef } from 'react';
import TableBase from 'common/components/table/table-base';
import { citationFilterConfig, citationsInitialColumns } from './citations.config';
import citationService from 'common/services/citation-service/citation.service';
import styles from './citations.module.scss';
import { CitationStatus } from 'features/eiq-manage/enums/citation-status.enum';
import { ReactComponent as ApproveIcon } from 'assets/icons/approve.svg';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { ReactComponent as MoveOut } from 'assets/icons/move-white.svg';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import MoveCitationModal from './move-citation-modal/move-citation-modal';
import { useReactToPrint } from 'react-to-print';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import EditCitationModal from './edit-citation-modal/edit-citation-modal';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { notification } from 'common/utils/notification';
import { ColumnFilter } from 'common/components/table/filters/column-filter/column-filter';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import exportService from 'common/services/export-service/export.service';
import { downloadCsv } from 'common/helpers/downloadCsv.helper';
import CitationPrint from './print-citation/citation-print';
import configurationStore from 'common/stores/configuration/configuration.store';
import { usePDF } from '@react-pdf/renderer';
import { trackPromise } from 'react-promise-tracker';
import { saveAs } from 'file-saver';
import PrintCitationList from './print-citation-list/print-citation-list';
import { IResponseList } from 'common/models/response-list.interface';
import dayjs from 'dayjs';

const TABLE_NAME = 'citations';

export const Citations = observer(() => {
  const [isApproveButtonDisabled, setApproveButtonDisabled] = useState(false);
  const [isCancelButtonDisabled, setCancelButtonDisables] = useState(false);
  const [isConfirmPaymentButtonDisabled, setConfirmPaymentButtonDisabled] = useState(false);

  const tableControls = useTableControls({
    onFetch: citationService.getCitations,
    initialColumns: filterColumnsByPersonalization(citationsInitialColumns, TABLE_NAME),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME),
    onUpdateColumns: async (columns: any[], sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  const getRowClassName = (rowIndex: number, rowData: any): string => {
    return rowData?.residentId ? '' : styles.missingTrafficLogRow;
  };

  const onConfirmPaymentClick = () => {
    const selectedRow = tableControls.getSelectedRow();
    citationService.confirmPayment(selectedRow.id).then(() => {
      tableControls.updateSelectedRow({ ...selectedRow, status: CitationStatus.Paid });
    });
  };

  const onCancelClick = () => {
    const selectedRow = tableControls.getSelectedRow();
    citationService.cancel(selectedRow.id).then(() => {
      if (
        selectedRow.status === CitationStatus.PayNow ||
        selectedRow.status === CitationStatus.PastDue ||
        selectedRow.status === CitationStatus.Delinquent
      ) {
        notification.success({ message: 'Citation and the related suspension is cancelled.', duration: 5 });
      }
      tableControls.updateSelectedRow({ ...selectedRow, status: CitationStatus.Canceled });
    });
  };

  const onApproveClick = () => {
    const selectedRow = tableControls.getSelectedRow();
    citationService.approve(selectedRow.id).then(() => {
      tableControls.updateSelectedRow({ ...selectedRow, status: CitationStatus.PayNow });
    });
  };

  useEffect(() => {
    const row: ICitationRead = tableControls.getSelectedRow();

    if (!row?.residentId) {
      setApproveButtonDisabled(true);
      setCancelButtonDisables(true);
      setConfirmPaymentButtonDisabled(true);
    } else {
      const isCancelDisables =
        row.status === CitationStatus.Canceled || row.status === CitationStatus.Released || row.status === CitationStatus.Paid;
      setCancelButtonDisables(isCancelDisables);

      const isApproveDisabled = row.status !== CitationStatus.Pending;
      setApproveButtonDisabled(isApproveDisabled);

      const isConfirmPaymentDisabled = row.status !== CitationStatus.PayNow && row.status !== CitationStatus.Delinquent;
      setConfirmPaymentButtonDisabled(isConfirmPaymentDisabled);
    }
  }, [tableControls.getSelectedRow()]);

  const moveCitationModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const selectedRow: ICitationRead = tableControls.getSelectedRow();

      if (!selectedRow) {
        return;
      }
      const data = {
        citationId: selectedRow.id,
      };
      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        tableControls.refetchData();
        notification.success({ message: 'Citation was successfully moved.', duration: 5 });
      }
    },
  });

  const editCitationModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const selectedRow: ICitationRead = tableControls.getSelectedRow();

      if (!selectedRow) {
        return;
      }
      const data = {
        citationId: selectedRow.id,
        licenseNumber: selectedRow.licensePlate,
        licenseState: selectedRow.licenseState,
        ownerType: selectedRow.ownerType,
      };
      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        tableControls.refetchData();
        notification.success({ message: 'Citation was successfully updated.', duration: 5 });
      }
    },
  });

  const printRef = useRef<any>();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const isPdfReady = useRef<boolean>(false);
  const [pdfInstance, updatePdfInstance] = usePDF();
  const pdfInstanceRef = useRef(pdfInstance);

  useEffect(() => {
    if (!pdfInstance.loading && pdfInstance.url && pdfInstance.blob) {
      isPdfReady.current = true;
    } else {
      isPdfReady.current = false;
    }

    pdfInstanceRef.current = pdfInstance;
  }, [pdfInstance]);

  const waitForPdfReady = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const timer = setInterval(() => {
        if (isPdfReady.current) {
          clearInterval(timer);
          resolve();
        }

        if (Date.now() - start > 240000) {
          clearInterval(timer);
          reject(new Error('PDF generation timeout'));
        }
      }, 200);
    });
  };

  const getTableFilters = () => {
    const tableFilters = tableControls.getFilters();
    const tableSortBy = tableControls.getSortBy();
    const pageLimit = { page: 1, limit: 10_000 };

    return { tableFilters, tableSortBy, pageLimit };
  };

  const generatePdf = async (dateFrom: any, dateTo: any): Promise<void> => {
    const { pageLimit, tableFilters, tableSortBy } = getTableFilters();
    const data: IResponseList<ICitationRead> = await citationService.getCitations(pageLimit, tableFilters, tableSortBy);
    isPdfReady.current = false;
    updatePdfInstance(<PrintCitationList citations={data.items} dateFrom={dateFrom} dateTo={dateTo} />);
    await waitForPdfReady();
  };

  const onExportPdfClicked = async () => {
    const filterStr = tableControls.getFilters();

    let dates: any;

    if (filterStr) {
      dates = filterStr.match(/\d{4}-\d{2}-\d{2}/g);
    }

    let dateFrom = dayjs().subtract(1, 'year');
    let dateTo = dayjs();

    if (dates?.length) {
      dateFrom = dayjs(dates[0]);
      dateTo = dayjs(dates[1]);
    }

    await trackPromise(generatePdf(dateFrom.format('MM/DD/YYYY'), dateTo.format('MM/DD/YYYY')));
    if (pdfInstanceRef.current.blob) {
      saveAs(pdfInstanceRef.current.blob, `IntelliTicket-Violations_${dateFrom.format('MM-DD-YYYY')}_${dateTo.format('MM-DD-YYYY')}.pdf`);
    }
  };

  const onExportCsvCliked = async () => {
    const { pageLimit, tableFilters, tableSortBy } = getTableFilters();
    const blob = await exportService.processCitations(pageLimit, tableFilters, tableSortBy);
    downloadCsv(blob, 'citations.csv');
  };

  return (
    <>
      {tableControls.getSelectedRow() && <CitationPrint ref={printRef} citation={tableControls.getSelectedRow()} />}
      <TableContextProvider controls={tableControls}>
        <Card className="eiq-card eiq-card-border full-card">
          <Row className="eiq-card-header">
            <Col span={24}>
              <h1 className="h1">Citations</h1>
            </Col>
          </Row>
          <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
            <Col flex="auto">
              <SearchFilter
                placeholder="Search by resident name, license plate, guest name, citation #"
                rulesForColumn={{
                  licensePlate: FilterTypesEnum.Contains,
                  guestName: FilterTypesEnum.Contains,
                  residentName: FilterTypesEnum.Contains,
                  externalId: FilterTypesEnum.Contains,
                }}
              />
            </Col>
            <Col>
              <Space size={8}>
                <Button type="primary" size="middle" onClick={onExportCsvCliked} className="mobile-btn">
                  Export CSV
                </Button>
                <Button type="primary" size="middle" onClick={onExportPdfClicked} className="mobile-btn">
                  Export PDF
                </Button>
                <Button type="default" icon={<PrintIcon />} size={'middle'} onClick={handlePrint} className="mobile-btn">
                  Print
                </Button>
                <Button
                  type="default"
                  icon={<EditIcon />}
                  className="mobile-btn"
                  size="middle"
                  onClick={editCitationModal.showModal}
                  disabled={tableControls.getSelectedRow()?.status !== CitationStatus.Pending}>
                  Edit
                </Button>
                <Button
                  type="primary"
                  className="mobile-btn"
                  size="middle"
                  icon={<MoveOut />}
                  onClick={moveCitationModal.showModal}
                  disabled={
                    (!tableControls.getSelectedRow()?.guestName && !tableControls.getSelectedRow()?.companyName) ||
                    tableControls.getSelectedRow()?.status !== CitationStatus.Pending
                  }>
                  Move
                </Button>
                <Button
                  icon={<ApproveIcon />}
                  size="middle"
                  type="primary"
                  onClick={onApproveClick}
                  disabled={isApproveButtonDisabled}
                  className={styles.approveButton}>
                  Approve
                </Button>
                {!configurationStore.configuration?.citations.isAutoIssueInvoice && (
                  <Button
                    icon={<ApproveIcon />}
                    className="mobile-btn"
                    size="middle"
                    type="primary"
                    onClick={onConfirmPaymentClick}
                    disabled={isConfirmPaymentButtonDisabled}>
                    Confirm payment
                  </Button>
                )}
                <Button
                  icon={<RejectIcon />}
                  className="mobile-btn"
                  size="middle"
                  type="primary"
                  onClick={onCancelClick}
                  danger
                  disabled={isCancelButtonDisabled}>
                  Cancel
                </Button>
                <ColumnVisibility isColumnReorderAllow={true} />
                <ColumnFilter filterConfig={citationFilterConfig} />
              </Space>
            </Col>
          </Row>
          <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault rowClassName={getRowClassName} />
        </Card>
      </TableContextProvider>
      <MoveCitationModal
        initData={moveCitationModal?.initData}
        title="Move citation"
        isOpen={moveCitationModal?.isOpenModal}
        onClose={moveCitationModal?.hideModal}
      />
      <EditCitationModal
        initData={editCitationModal?.initData}
        title="Edit License Plate Number and Type"
        isOpen={editCitationModal?.isOpenModal}
        onClose={editCitationModal?.hideModal}
      />
    </>
  );
});
